import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Mail, Plus } from "lucide-react";
import { PLAN_BY_ID, PLANS } from "@metrickle/schema/plans";
import { api, ApiError } from "../../lib/api";
import {
  ACCOUNT_STAGE_LABEL,
  LIFECYCLE_LABEL,
  roleLabel,
  useAccount,
  useAccounts,
  useAdminConfig,
  type AccountListRow,
  type AccountStage,
  type Lifecycle,
} from "../../lib/admin";
import { fmtNumber, PLATFORM_LABEL } from "../../lib/format";
import { StatTile } from "../../components/stats";
import { StartSupportButton, SupportHistory } from "../../components/support";
import {
  Ago,
  BackLink,
  Badge,
  Button,
  Card,
  CardHeader,
  CardList,
  CardRow,
  CardSection,
  Checkbox,
  Delta,
  Drawing,
  Empty,
  ErrorState,
  IconButton,
  None,
  PageHeader,
  SaveStatus,
  SearchInput,
  Segmented,
  SelectField,
  Skeleton,
  SortHeader,
  StatGrid,
  Table,
  TableEmptyRow,
  TableScroll,
  TableSkeletonRows,
  Textarea,
  notifyError,
  useSaveStatus,
  useTableSort,
} from "../../components/ui";
import {
  AccountStageBadge,
  ComposeModal,
  usePageTitle,
  FollowUpInput,
  Health,
  LifecycleBadge,
  LogActivity,
  PlayChips,
  SignalChips,
  TaskList,
  Timeline,
  invalidateAdmin,
} from "./components";
import { OnboardModal, PendingInvites, PlanCard, RegionCard } from "./plan";

type SortKey = "name" | "health" | "events7d" | "trend" | "apps" | "members" | "lastEventAt" | "createdAt";
const SORTERS: Record<SortKey, (r: AccountListRow) => number | string> = {
  name: (r) => r.name.toLowerCase(),
  health: (r) => r.health,
  events7d: (r) => r.usage.events7d,
  trend: (r) => r.trend ?? -Infinity,
  apps: (r) => r.usage.apps,
  members: (r) => r.usage.members,
  lastEventAt: (r) => r.usage.lastEventAt ?? 0,
  createdAt: (r) => r.createdAt,
};

// Same words as the lifecycle badges in the table.
const LIFECYCLE_FILTERS = [
  { value: "all", label: "All" },
  ...(["active", "dormant", "not_installed", "no_app", "churned"] satisfies Lifecycle[]).map((v) => ({ value: v, label: LIFECYCLE_LABEL[v] })),
];

export function AdminAccounts() {
  const q = useAccounts();
  usePageTitle("Accounts");
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [life, setLife] = useState("all");
  const [stage, setStage] = useState("all");
  const [plan, setPlan] = useState("all");
  const [onboard, setOnboard] = useState(false);
  // A new column starts A–Z for names, largest/newest first otherwise.
  const { sort, sortBy, sorted } = useTableSort<SortKey>({ key: "health", dir: -1 }, (k) => (k === "name" ? 1 : -1));

  const rows = useMemo(() => {
    const s = search.trim().toLowerCase();
    const filtered = (q.data ?? [])
      .filter((a) => life === "all" || a.lifecycle === life)
      .filter((a) => stage === "all" || a.stage === stage)
      .filter((a) => plan === "all" || a.plan === plan)
      .filter(
        (a) =>
          !s ||
          a.name.toLowerCase().includes(s) ||
          a.owner?.email.toLowerCase().includes(s) ||
          a.memberNames.some((n) => n.toLowerCase().includes(s)) ||
          a.appNames.some((n) => n.toLowerCase().includes(s)),
      );
    return sorted(filtered, (r, k) => SORTERS[k](r));
  }, [q.data, search, life, stage, plan, sort]); // `sorted` reads `sort`


  return (
    <>
      <PageHeader
        title="Accounts"
        subtitle="Every workspace on the platform, scored from real usage."
        actions={
          <Button variant="primary" onClick={() => setOnboard(true)}>
            <Plus size={14} /> Onboard customer
          </Button>
        }
      />
      <OnboardModal open={onboard} onOpenChange={setOnboard} />
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <SearchInput label="Search accounts" wrapperClassName="w-full @xl:w-72" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search accounts, people, apps" />
        <div className="relative overflow-x-auto">
          <Segmented aria-label="Status filter" items={LIFECYCLE_FILTERS} value={life} onChange={setLife} />
        </div>
        <SelectField
          aria-label="Pipeline stage"
          className="w-40"
          items={[{ value: "all", label: "Any stage" }, ...Object.entries(ACCOUNT_STAGE_LABEL).map(([value, label]) => ({ value, label }))]}
          value={stage}
          onChange={setStage}
        />
        <SelectField
          aria-label="Plan"
          className="w-36"
          items={[{ value: "all", label: "Any plan" }, ...PLANS.map((p) => ({ value: p.id, label: p.name }))]}
          value={plan}
          onChange={setPlan}
        />
        <span className="ml-auto text-xs text-ink-3" role="status" aria-live="polite">
          {q.data && `${rows.length} of ${q.data.length} accounts`}
        </span>
      </div>
      <Card>
        {q.isError ? (
          <ErrorState error={q.error} />
        ) : (
          <TableScroll label="Accounts">
            <Table caption="Accounts, sortable by column. Select an account name to open it." busy={q.isPending} className="min-w-[1060px]">
              <thead>
                <tr>
                  <SortHeader {...sortBy("name")}>Account</SortHeader>
                  <th scope="col">Status</th>
                  <th scope="col">Plan</th>
                  <SortHeader {...sortBy("health")}>Health</SortHeader>
                  <SortHeader {...sortBy("events7d")} align="end">Events 7d</SortHeader>
                  <SortHeader {...sortBy("trend")} align="end">
                    <span aria-hidden>W/w</span>
                    <span className="sr-only">Week-over-week change</span>
                  </SortHeader>
                  <SortHeader {...sortBy("apps")} align="end">Apps</SortHeader>
                  <SortHeader {...sortBy("members")} align="end">Team</SortHeader>
                  <SortHeader {...sortBy("lastEventAt")}>Last data</SortHeader>
                  <SortHeader {...sortBy("createdAt")}>Signed up</SortHeader>
                  <th scope="col">Signals</th>
                </tr>
              </thead>
              <tbody className="tabular">
                {q.isPending && <TableSkeletonRows columns={11} />}
                {rows.map((a) => (
                  <tr key={a.id} className="cursor-pointer hover:bg-surface-2" onClick={() => navigate(`/admin/accounts/${a.id}`)}>
                    <td>
                      <Link to={`/admin/accounts/${a.id}`} className="font-medium text-ink hover:underline" onClick={(e) => e.stopPropagation()}>
                        {a.name}
                      </Link>
                      <div className="truncate text-xs text-ink-3">{a.owner?.email}</div>
                    </td>
                    <td>
                      <div className="flex flex-col items-start gap-1">
                        <LifecycleBadge value={a.lifecycle} />
                        <span className="text-[11px] text-ink-3">{ACCOUNT_STAGE_LABEL[a.stage]}</span>
                      </div>
                    </td>
                    <td className="text-xs text-ink-2">{PLAN_BY_ID[a.plan].name}</td>
                    <td>
                      <Health score={a.health} compact />
                    </td>
                    <td className="text-right text-ink">{fmtNumber(a.usage.events7d)}</td>
                    <td className="text-right">
                      {a.trend === null ? <None label="No previous week" /> : <Delta current={a.usage.events7d} previous={a.usage.eventsPrev7d} />}
                    </td>
                    <td className="text-right">{a.usage.apps}</td>
                    <td className="text-right">{a.usage.members}</td>
                    <td className="text-xs text-ink-2">
                      <Ago t={a.usage.lastEventAt} />
                    </td>
                    <td className="text-xs text-ink-2">
                      <Ago t={a.createdAt} />
                    </td>
                    <td>
                      <SignalChips signals={a.signals} max={2} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {q.data && !rows.length && <Empty title="No accounts match">Try a different filter or search.</Empty>}
          </TableScroll>
        )}
      </Card>
    </>
  );
}

export function AdminAccount() {
  const { accountId } = useParams();
  const q = useAccount(accountId!);
  const config = useAdminConfig();
  const qc = useQueryClient();
  const a = q.data;
  const [notes, setNotes] = useState("");
  const [compose, setCompose] = useState<{ id: string; name: string; email: string } | null>(null);
  const notesSave = useSaveStatus();
  const statusSave = useSaveStatus();
  useEffect(() => setNotes(a?.notes ?? ""), [a?.notes]);
  usePageTitle(a?.name);

  /** Resolves false (after a toast) when the save fails. */
  const patch = async (json: Record<string, unknown>) => {
    try {
      await api(`/admin/accounts/${accountId}`, { method: "PATCH", json });
      await invalidateAdmin(qc);
      return true;
    } catch (err) {
      notifyError(err);
      return false;
    }
  };

  /** Stage and follow-up apply instantly; SaveStatus beside them announces it. */
  const patchStatus = (json: Record<string, unknown>) =>
    void statusSave.track(async () => {
      if (!(await patch(json))) throw new Error("save failed");
    });

  if (q.isError)
    return q.error instanceof ApiError && q.error.status === 404 ? (
      <Empty title="Account not found" illustration={<Drawing name="notFound" />} />
    ) : (
      <Card>
        <ErrorState title="Couldn't load this account" error={q.error} />
      </Card>
    );

  return (
    <>
      <BackLink to="/admin/accounts" className="mb-3">
        All accounts
      </BackLink>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-2">
            {a?.name ?? "…"} {a && <LifecycleBadge value={a.lifecycle} />} {a && <AccountStageBadge value={a.stage} />}
          </span>
        }
        subtitle={a && <>Workspace created <Ago t={a.createdAt} /> · owner {a.owner ? `${a.owner.name} (${a.owner.email})` : "none"}</>}
      />
      {a && (
        <div className="-mt-2 mb-5 flex flex-wrap items-center gap-2">
          <SelectField
            aria-label="Pipeline stage"
            className="w-36"
            items={(config.data?.accountStages ?? []).map((s) => ({ value: s, label: ACCOUNT_STAGE_LABEL[s] }))}
            value={a.stage}
            onChange={(v) => patchStatus({ stage: v as AccountStage })}
          />
          <FollowUpInput value={a.nextFollowUpAt} onChange={(t) => patchStatus({ nextFollowUpAt: t })} />
          <SaveStatus status={statusSave.status} />
          <StartSupportButton organizationId={a.id} organizationName={a.name} />
          {a.owner?.contactId && (
            <Button variant="primary" onClick={() => setCompose({ id: a.owner!.contactId!, name: a.owner!.name, email: a.owner!.email })}>
              <Mail size={14} /> Email owner
            </Button>
          )}
        </div>
      )}
      {!a ? (
        <Skeleton className="h-96" />
      ) : (
        <div className="grid gap-4 @4xl:grid-cols-[1fr_380px]">
          <div className="min-w-0 space-y-4">
            <Card>
              <StatGrid>
                <StatTile label="Events 7d" value={fmtNumber(a.usage.events7d)} current={a.usage.events7d} previous={a.usage.eventsPrev7d} />
                <StatTile label="Events 30d" value={fmtNumber(a.usage.events30d)} />
                <StatTile label="Visitors 30d" value={fmtNumber(a.usage.visitors30d)} />
                <StatTile label="Health" value={a.health} hint="Recency, volume, trend, team and apps" />
                <StatTile label="Last data" value={<span className="text-base"><Ago t={a.usage.lastEventAt} /></span>} />
                <StatTile label="Last login" value={<span className="text-base"><Ago t={a.lastSeenAt} /></span>} />
              </StatGrid>
              <CardSection className="flex flex-wrap items-center gap-2">
                <SignalChips signals={a.signals} max={10} />
                <PlayChips plays={a.plays} />
              </CardSection>
            </Card>

            <Card>
              <CardHeader title="Apps" subtitle="Usage per app, from the event store" />
              <TableScroll label="Apps" divided>
                <Table caption="Usage per app" className="min-w-[520px]">
                  <thead>
                    <tr>
                      <th scope="col">App</th>
                      <th scope="col" className="text-right">Events 7d</th>
                      <th scope="col" className="text-right">Events 30d</th>
                      <th scope="col" className="text-right">Visitors 30d</th>
                      <th scope="col">Last data</th>
                    </tr>
                  </thead>
                  <tbody className="tabular">
                    {a.apps.map((app) => (
                      <tr key={app.id}>
                        <td>
                          <span className="font-medium text-ink">{app.name}</span> <Badge>{PLATFORM_LABEL[app.platform] ?? app.platform}</Badge>
                        </td>
                        <td className="text-right">{fmtNumber(app.usage?.events7d ?? 0)}</td>
                        <td className="text-right">{fmtNumber(app.usage?.events30d ?? 0)}</td>
                        <td className="text-right">{fmtNumber(app.usage?.visitors30d ?? 0)}</td>
                        <td className="text-xs text-ink-2">
                          <Ago t={app.usage?.lastEventAt} empty="never" />
                        </td>
                      </tr>
                    ))}
                    {!a.apps.length && <TableEmptyRow columns={5}>No apps yet. This is the "Stuck before first app" play.</TableEmptyRow>}
                  </tbody>
                </Table>
              </TableScroll>
            </Card>

            <Card>
              <CardHeader title="People" subtitle="Everyone in this workspace" />
              <CardList>
                {a.members.map((m) => (
                  <CardRow key={m.userId}>
                    <div className="min-w-0">
                      {m.contactId ? (
                        <Link to={`/admin/people/${m.contactId}`} className="text-sm font-medium text-ink hover:underline">
                          {m.name}
                        </Link>
                      ) : (
                        <span className="text-sm font-medium text-ink">{m.name}</span>
                      )}
                      <div className="truncate text-xs text-ink-3">
                        {m.email} · last login <Ago t={m.lastSeenAt} />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge tone={m.role.includes("owner") ? "accent" : "neutral"}>{roleLabel(m.role)}</Badge>
                      {m.contactId && (
                        <IconButton label={`Email ${m.name}`} onClick={() => setCompose({ id: m.contactId!, name: m.name, email: m.email })}>
                          <Mail size={13} />
                        </IconButton>
                      )}
                    </div>
                  </CardRow>
                ))}
              </CardList>
            </Card>

            <PlanCard account={a} />
            <RegionCard account={a} />
            <PendingInvites invitations={a.invitations} />

            <Card>
              <CardHeader title="Add-ons" subtitle="Paid extras for this workspace. Changes reach their apps within a minute." />
              <div className="border-t border-line px-4 py-3">
                <Checkbox
                  checked={a.addons.whiteLabel}
                  onCheckedChange={(v) => void patch({ whiteLabel: v })}
                  hint={'Lets the workspace hide "Powered by Metrickle" and set its own brand colour on the feedback widget, surveys and study pages. Turning it off restores Metrickle branding but keeps their saved choices.'}
                >
                  White-label
                </Checkbox>
              </div>
            </Card>

            <Card>
              <CardHeader title={<label htmlFor="account-notes">Account notes</label>} subtitle="Plan, pricing context, what they care about. Saves when you leave the field." actions={<SaveStatus status={notesSave.status} />} />
              <div className="px-4 pb-4">
                <Textarea
                  id="account-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  onBlur={() =>
                    notes !== (a.notes ?? "") &&
                    notesSave.track(async () => {
                      if (!(await patch({ notes }))) throw new Error("save failed");
                    })
                  }
                  rows={4}
                />
              </div>
            </Card>
          </div>

          <div className="space-y-4">
            <Card>
              <CardHeader title="Tasks" />
              <CardSection flush>
                <TaskList tasks={a.tasks} organizationId={a.id} />
              </CardSection>
            </Card>
            <SupportHistory orgId={a.id} subtitle="Read-only support sessions. The workspace's owners and admins see this list too." />
            <Card>
              <CardHeader title="Activity" subtitle="This account and its people" />
              <LogActivity path={`/admin/accounts/${a.id}/activities`} />
              <CardSection flush>
                <Timeline items={a.activity} showWho />
              </CardSection>
            </Card>
          </div>
        </div>
      )}
      {compose && <ComposeModal open onOpenChange={(o) => !o && setCompose(null)} contact={compose} />}
    </>
  );
}
